import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, CheckCircle, Loader2 } from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { apiFetch } from '../lib/api';

interface StockWarningProduct {
  id: number;
  name: string;
  stockQuantity: number;
  isActive: number;
  supplierId: number | null;
  threshold: number;
  notifyEnabled: number;
  isBelow: boolean;
}

interface RowEdit {
  threshold: string;
  notifyEnabled: boolean;
  stockInput: string;
  saving: boolean;
  saved: boolean;
  error: string | null;
  stockSaving: boolean;
  stockSaved: boolean;
  stockError: string | null;
}

function makeEdit(p: StockWarningProduct): RowEdit {
  return {
    threshold: String(p.threshold),
    notifyEnabled: p.notifyEnabled === 1,
    stockInput: String(p.stockQuantity),
    saving: false,
    saved: false,
    error: null,
    stockSaving: false,
    stockSaved: false,
    stockError: null,
  };
}

export default function SupplierStockWarnings() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [products, setProducts] = useState<StockWarningProduct[]>([]);
  const [edits, setEdits] = useState<Record<number, RowEdit>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const handleUnauthorized = () => {
    localStorage.removeItem('admin_token');
    navigate('/admin');
  };

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch('/api/public/supplier/stock-warnings');
      if (res.status === 401) {
        handleUnauthorized();
        return;
      }
      const data = await res.json();
      if (!res.ok || data.error) {
        setError(data.error || t('partner:supplier.stock.loadFailed'));
        return;
      }
      const list = data as StockWarningProduct[];
      setProducts(list);
      const next: Record<number, RowEdit> = {};
      for (const p of list) next[p.id] = makeEdit(p);
      setEdits(next);
    } catch {
      setError(t('partner:supplier.stock.networkError'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!localStorage.getItem('admin_token')) {
      navigate('/admin');
      return;
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateEdit = (id: number, patch: Partial<RowEdit>) => {
    setEdits(prev => (prev[id] ? { ...prev, [id]: { ...prev[id], ...patch } } : prev));
  };

  const handleSaveSettings = async (p: StockWarningProduct) => {
    const edit = edits[p.id];
    if (!edit) return;
    const threshold = Math.max(0, parseInt(edit.threshold, 10) || 0);
    updateEdit(p.id, { saving: true, error: null, saved: false });
    try {
      const res = await apiFetch(`/api/public/supplier/products/${p.id}/stock-settings`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ lowStockThreshold: threshold, notifyEnabled: edit.notifyEnabled }),
      });
      if (res.status === 401) {
        handleUnauthorized();
        return;
      }
      const data = await res.json();
      if (!res.ok || data.error) {
        updateEdit(p.id, { saving: false, error: data.error || t('partner:supplier.stock.saveFailed') });
        return;
      }
      updateEdit(p.id, { saving: false, saved: true, threshold: String(threshold) });
      setProducts(prev => prev.map(x => x.id === p.id
        ? { ...x, threshold, notifyEnabled: edit.notifyEnabled ? 1 : 0, isBelow: x.stockQuantity < threshold }
        : x));
      setTimeout(() => updateEdit(p.id, { saved: false }), 2000);
    } catch {
      updateEdit(p.id, { saving: false, error: t('partner:supplier.stock.networkError') });
    }
  };

  const handleStockUpdate = async (p: StockWarningProduct) => {
    const edit = edits[p.id];
    if (!edit) return;
    const qty = Math.max(0, parseInt(edit.stockInput, 10) || 0);
    updateEdit(p.id, { stockSaving: true, stockError: null, stockSaved: false });
    try {
      const res = await apiFetch(`/api/public/admin/products/${p.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ stock_quantity: qty }),
      });
      if (res.status === 401) {
        handleUnauthorized();
        return;
      }
      const data = await res.json();
      if (!res.ok || data.error) {
        updateEdit(p.id, { stockSaving: false, stockError: data.error || t('partner:supplier.stock.updateFailed') });
        return;
      }
      updateEdit(p.id, { stockSaving: false, stockSaved: true, stockInput: String(qty) });
      setProducts(prev => prev.map(x => x.id === p.id
        ? { ...x, stockQuantity: qty, isBelow: qty < x.threshold }
        : x));
      setTimeout(() => updateEdit(p.id, { stockSaved: false }), 2000);
    } catch {
      updateEdit(p.id, { stockSaving: false, stockError: t('partner:supplier.stock.networkError') });
    }
  };

  const belowProducts = products.filter(p => p.isBelow);

  return (
    <AdminLayout currentPage="supplier-stock-warnings" onLogout={handleUnauthorized}>
      <div className="p-4 md:p-8">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
              <AlertTriangle className="w-6 h-6 text-orange-600" />
              {t('partner:supplier.stock.title')}
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {t('partner:supplier.stock.intro')}
            </p>
          </div>
          <LanguageSwitcher size="sm" />
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 dark:bg-red-900/20 dark:border-red-800 dark:text-red-300">
            {error}
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-16 text-gray-500 dark:text-gray-400">
            <Loader2 className="w-6 h-6 animate-spin mr-2" />
            {t('partner:supplier.stock.loading')}
          </div>
        ) : (
          <>
            {/* 警告卡片区 */}
            {belowProducts.length === 0 ? (
              <div className="mb-6 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-green-700 dark:text-green-300 flex items-center gap-2">
                <CheckCircle className="w-5 h-5 shrink-0" />
                {t('partner:supplier.stock.allGood')}
              </div>
            ) : (
              <div className="mb-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {belowProducts.map(p => (
                  <div key={p.id} className="p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                    <div className="flex items-start gap-2">
                      <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-red-800 dark:text-red-200 truncate">{p.name}</div>
                        <div className="mt-1 flex items-baseline gap-2">
                          <span className="text-3xl font-bold text-red-600 dark:text-red-400">{p.stockQuantity}</span>
                          <span className="text-sm text-red-700 dark:text-red-300">{t('partner:supplier.stock.currentStock')}</span>
                        </div>
                        <div className="text-sm text-red-700 dark:text-red-300 mt-1">
                          {t('partner:supplier.stock.thresholdGap', { threshold: p.threshold, gap: p.threshold - p.stockQuantity })}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* 產品設置表 */}
            {products.length === 0 ? (
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-8 text-center text-gray-500 dark:text-gray-400">
                {t('partner:supplier.stock.noProducts')}
              </div>
            ) : (
              <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50 dark:bg-gray-700/50 text-gray-600 dark:text-gray-400">
                      <tr>
                        <th className="px-4 py-3 text-left font-medium">{t('partner:supplier.stock.colProduct')}</th>
                        <th className="px-4 py-3 text-left font-medium">{t('partner:supplier.stock.colCurrent')}</th>
                        <th className="px-4 py-3 text-left font-medium">{t('partner:supplier.stock.colThreshold')}</th>
                        <th className="px-4 py-3 text-center font-medium">{t('partner:supplier.stock.colNotify')}</th>
                        <th className="px-4 py-3 text-left font-medium">{t('partner:supplier.stock.colSettings')}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {products.map(p => {
                        const edit = edits[p.id];
                        if (!edit) return null;
                        return (
                          <tr key={p.id} className={p.isBelow ? 'border-l-4 border-l-red-500' : ''}>
                            <td className="px-4 py-3">
                              <span className="font-medium text-gray-800 dark:text-white">{p.name}</span>
                              {p.isActive === 0 && (
                                <span className="ml-2 inline-block px-2 py-0.5 text-xs rounded bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400">
                                  {t('partner:supplier.stock.inactive')}
                                </span>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <input
                                  type="number"
                                  min={0}
                                  value={edit.stockInput}
                                  onChange={e => updateEdit(p.id, { stockInput: e.target.value, stockSaved: false, stockError: null })}
                                  className="w-20 px-2 py-1 border border-gray-200 rounded bg-white text-gray-800 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 dark:bg-gray-800 dark:text-white dark:border-gray-600"
                                />
                                <button
                                  onClick={() => handleStockUpdate(p)}
                                  disabled={edit.stockSaving}
                                  className="px-3 py-1 text-xs rounded bg-orange-600 hover:bg-orange-700 text-white disabled:opacity-50 flex items-center gap-1"
                                >
                                  {edit.stockSaving && <Loader2 className="w-3 h-3 animate-spin" />}
                                  {t('partner:supplier.stock.updateBtn')}
                                </button>
                              </div>
                              {edit.stockSaved && (
                                <div className="text-xs text-green-600 dark:text-green-400 mt-1">{t('partner:supplier.stock.updated')}</div>
                              )}
                              {edit.stockError && (
                                <div className="text-xs text-red-600 dark:text-red-400 mt-1">{edit.stockError}</div>
                              )}
                            </td>
                            <td className="px-4 py-3">
                              <input
                                type="number"
                                min={0}
                                value={edit.threshold}
                                onChange={e => updateEdit(p.id, { threshold: e.target.value, saved: false, error: null })}
                                className="w-20 px-2 py-1 border border-gray-200 rounded bg-white text-gray-800 focus:ring-2 focus:ring-orange-500 focus:border-orange-500 dark:bg-gray-800 dark:text-white dark:border-gray-600"
                              />
                            </td>
                            <td className="px-4 py-3 text-center">
                              <button
                                type="button"
                                role="switch"
                                aria-checked={edit.notifyEnabled}
                                aria-label={t('partner:supplier.stock.notifySwitch')}
                                onClick={() => updateEdit(p.id, { notifyEnabled: !edit.notifyEnabled, saved: false, error: null })}
                                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${edit.notifyEnabled ? 'bg-orange-600' : 'bg-gray-300 dark:bg-gray-600'}`}
                              >
                                <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${edit.notifyEnabled ? 'translate-x-6' : 'translate-x-1'}`} />
                              </button>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => handleSaveSettings(p)}
                                  disabled={edit.saving}
                                  className="px-3 py-1 text-xs rounded bg-orange-600 hover:bg-orange-700 text-white disabled:opacity-50 flex items-center gap-1"
                                >
                                  {edit.saving && <Loader2 className="w-3 h-3 animate-spin" />}
                                  {t('partner:supplier.stock.saveBtn')}
                                </button>
                                {edit.saved && (
                                  <span className="text-xs text-green-600 dark:text-green-400 flex items-center gap-1">
                                    <CheckCircle className="w-3 h-3" />
                                    {t('partner:supplier.stock.saved')}
                                  </span>
                                )}
                              </div>
                              {edit.error && (
                                <div className="text-xs text-red-600 dark:text-red-400 mt-1">{edit.error}</div>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </AdminLayout>
  );
}
